// src/pages/Inventory/STNOutward.jsx
// ✅ Converted from: STNOutward.aspx + STNOutward.aspx.cs + INV_STNOutward.cs
// ✅ Uses Node routes: /api/inventory/stn-outward-entry/*
// ✅ NO rights logic (excluded as instructed)
// ✅ Pagination added on gv_itemlist (search results grid)
// ✅ All business logic preserved:
//    - Mode detection: sessionStorage "stnoutward" → Edit mode, else New mode
//    - Code generation: StoreName/YEAR/STNOUT/0001
//    - ddl_filedata() / ddl_filedata1(): from store, to branch, to store, STN req no dropdowns
//    - Cascade: FromStore → ToBranch → ToStore → STNReqNo
//    - STNReqNo change → auto-fills fromStore, toBranch, toStore
//    - Search (Imgbtn_search_Click): filters by date/store/branch/reqno → gv_itemlist
//    - Item selection + Add → fillselecteditemsgrid (gv_outwardlist)
//    - Future date ledger check → warning list (lst_items)
//    - Transfer qty validation: > 0, ≤ availableStock, ≤ pendingQty
//    - Serial No link: opens modal (replaces popup window)
//    - FIFO pricing (server-side via submit)
//    - Concurrency check (server-side)
//    - Submit (new): return codes 1-6 → proper messages + redirect
//    - Duplicate key retry up to 10 times (case 3)
//    - Edit mode: fileexistingdata + fillexistingitemsgrid (gv_outlistedit)
//    - Edit submit: UpdateSTNoutward + UpdateSTNoutwardDet
//    - Close radio button (marks req as closed)
//    - Financial year date validation on currentDate field

import { useEffect, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Search, ChevronLeft, ChevronRight, RefreshCcw,
  ArrowLeft, CheckSquare, Square, AlertCircle, X,
  Send, Edit3, Info
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ─────────────────────── helpers ───────────────────────
const getTodayISO   = () => new Date().toISOString().slice(0, 10);
const getDaysAgoISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

function isoToDDMM(v) {
  if (!v) return "";
  const [yyyy, mm, dd] = v.split("-");
  return `${dd}/${mm}/${yyyy}`;
}
function ddmmToISO(v) {
  if (!v) return "";
  if (v.includes("-")) return v;
  const p = v.split("/");
  if (p.length === 3) return `${p[2]}-${p[1]}-${p[0]}`;
  return v;
}
function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}
function safeFloat(v, def = 0) {
  const n = parseFloat(v);
  return isNaN(n) ? def : n;
}

// ─────────────────────── Pagination ───────────────────────
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  let start = Math.max(1, page - 3);
  let end   = Math.min(totalPages, start + 6);
  if (end - start < 6) start = Math.max(1, end - 6);
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
      <button onClick={() => onPage(page - 1)} disabled={page === 1}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
        <ChevronLeft size={15} />
      </button>
      {start > 1 && (<>
        <button onClick={() => onPage(1)} className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">1</button>
        <span className="text-xs px-1">…</span>
      </>)}
      {pages.map(p => (
        <button key={p} onClick={() => onPage(p)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition
            ${p === page ? "bg-blue-600 text-white" : "hover:bg-gray-200 dark:hover:bg-gray-700"}`}>
          {p}
        </button>
      ))}
      {end < totalPages && (<>
        <span className="text-xs px-1">…</span>
        <button onClick={() => onPage(totalPages)} className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">{totalPages}</button>
      </>)}
      <button onClick={() => onPage(page + 1)} disabled={page === totalPages}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function STNOutward() {
  const navigate    = useNavigate();
  const { theme }   = useContext(ThemeContext);
  const isDark      = theme === "dark";
  const currentSkin = getCurrentSkin();
  const { baseColor, baseShade } = currentSkin;
  const primaryBtnClass   = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session / user ──
  const branchId    = Number(localStorage.getItem("branchId")  || 0);
  const finYearId   = Number(localStorage.getItem("finYearId") || 0);
  const loginUserId = localStorage.getItem("userId") || "";
  const username = localStorage.getItem("username") || "";
  const fy = localStorage.getItem("fyr") || ""; // "2023-2024"

let finYearStart = "";
let finYearEnd = "";

if (fy.includes("-")) {
  const [startYear, endYear] = fy.split("-");
  finYearStart = `01/04/${startYear}`;
  finYearEnd = `31/03/${endYear}`;
}

console.log(finYearStart); // 01/04/2023
console.log(finYearEnd);   // 31/03/2024

  // ── Edit mode detection (from sessionStorage, set by STNListings) ──
  const [isEditMode,        setIsEditMode]        = useState(false);
  const [sessionOutwardNo,  setSessionOutwardNo]  = useState("");
  const [sessionOutwardId,  setSessionOutwardId]  = useState("");

  // ── style helpers ──
  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/60 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");
  const inputDisabled = `w-full px-3 py-2 rounded-xl border outline-none text-sm cursor-not-allowed opacity-60 ` +
    (isDark ? "bg-gray-800/40 border-white/5 text-gray-400" : "bg-gray-100 border-gray-200 text-gray-500");
  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;
  const cardBase  = `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark ? "border-white/10 bg-gray-800/40 backdrop-blur-md" : "border-gray-200 bg-white");
  const thBase = `px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-white ${primaryBtnClass}`;
  const tdBase = `px-3 py-2 text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`;

  // ── Form Fields ──
  const [stnOutwardNo, setStnOutwardNo] = useState("");
  const [fromDate,     setFromDate]     = useState(getDaysAgoISO(7));
  const [toDate,       setToDate]       = useState(getTodayISO());
  const [currentDate,  setCurrentDate]  = useState(getTodayISO());
  const [fromStore,    setFromStore]    = useState("Select");
  const [toStore,      setToStore]      = useState("Select");
  const [toBranch,     setToBranch]     = useState("Select");
  const [stnReqNo,     setStnReqNo]     = useState("Select");
  const [remarks,      setRemarks]      = useState("");
  const [closeReq,     setCloseReq]     = useState(false); // rbtn_close

  // ── Dropdown data ──
  const [fromStores,  setFromStores]  = useState([]);
  const [toStores,    setToStores]    = useState([]);
  const [toBranches,  setToBranches]  = useState([]);
  const [stnReqNos,   setStnReqNos]   = useState([]);

  // ASP: ddl_filedata1() disables fromStore/toStore/toBranch until STNReqNo is selected
  const [storesDisabled, setStoresDisabled] = useState(true);


  // ── Loading states ──
  const [metaLoading,    setMetaLoading]    = useState(false);
  const [searchLoading,  setSearchLoading]  = useState(false);
  const [submitLoading,  setSubmitLoading]  = useState(false);

  // ── Grid 1: gv_itemlist (search results) ──
  const [itemList,     setItemList]     = useState([]);
  const [itemListPage, setItemListPage] = useState(1);
  const ITEM_PAGE_SIZE  = 10;
  const itemListTotal   = Math.ceil(itemList.length / ITEM_PAGE_SIZE);
  const itemListPaged   = itemList.slice((itemListPage-1)*ITEM_PAGE_SIZE, itemListPage*ITEM_PAGE_SIZE);
  const [itemListVisible, setItemListVisible] = useState(false);
  // Select all checkbox for itemList
  const [selectAllItems, setSelectAllItems] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  // ── Grid 2: gv_outwardlist (new entry items) ──
  const [outwardItems,  setOutwardItems]  = useState([]); // {fld_itemid, fld_itemcode, fld_itemname, fld_itemPartNo, fld_uomname, fld_closing_stock, datewiseqty, fld_pendingqty, fld_Qty, fld_STNRequestdetid, fld_branchitemid, fld_IsSerialNo, serialno, transferQty, checked}
  const [outwardVisible, setOutwardVisible] = useState(false);

  // ── Grid 3: gv_outlistedit (edit mode) ──
  const [editItems,    setEditItems]    = useState([]); // {fld_itemid, fld_branchitemid, fld_itemcode, fld_itemname, fld_itemPartNo, fld_uomname, fld_closing_stock, fld_Qty, fld_TransferQty, fld_STN_Outwarddetid, fld_fifoids, fld_fifoqtys, fld_fifoprices, fld_IsSerialNo, serialno, updateTransferQty}
  const [editVisible,  setEditVisible]  = useState(false);

  // ── Future date warning ──
  const [futureDateItems, setFutureDateItems] = useState([]); // list of strings
  const [showFutureWarn,  setShowFutureWarn]  = useState(false);

  // ── Serial No Modal ──
  const [serialModal, setSerialModal] = useState({ open: false, itemId: "", fromStoreId: "", qty: 0, mode: "new" }); // mode: "new" | "edit"

  // ── Visibility controls (from edit mode) ──
  const [showStnOutwardNoRow, setShowStnOutwardNoRow] = useState(false);
  const [searchVisible,       setSearchVisible]       = useState(true);

  // ════════════════════════════════════════════════
  // On mount: detect edit vs new mode
  // ASP: if (Session["stnoutward"] != null) → edit mode
  // ════════════════════════════════════════════════
  useEffect(() => {
    const stnOutward    = sessionStorage.getItem("stnoutward");
    const stnOutwardIdS = sessionStorage.getItem("Sessionwordwardid");
    const purReqNo      = sessionStorage.getItem("pur_stnrequestno");

    if (stnOutward) {
      setIsEditMode(true);
      setSessionOutwardNo(stnOutward);
      setSessionOutwardId(stnOutwardIdS || "");
      setStnOutwardNo(stnOutward);
      setShowStnOutwardNoRow(true);
      setSearchVisible(false);
      // load meta then existing data
      loadMeta(true).then(() => {
        loadExistingData(stnOutward, stnOutwardIdS || "");
      });
      sessionStorage.removeItem("stnoutward");
      sessionStorage.removeItem("Sessionwordwardid");
    } else {
      // New mode
      setShowStnOutwardNoRow(true);
      loadMeta(false).then(() => {
        generateCode();
      });
    }
    // eslint-disable-next-line
  }, []);

  // ════════════════════════════════════════════════
  // Load dropdown meta
  // ASP: ddl_filedata() / ddl_filedata1()
  // SPs: usp_GetBranchForDDL, usp_GetStoresByBranchid, usp_GetSTNReqNosOfBranch
  // ════════════════════════════════════════════════
  const loadMeta = useCallback(async (forEdit = false) => {
    setMetaLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/meta`, {
        params: { branchId, finYearId, forEdit: forEdit ? 1 : 0 },
      });
      if (res.data?.success) {
        setFromStores(res.data.fromStores || []);
        setToBranches(res.data.toBranches || []);
        setToStores(res.data.toStores || []);
        setStnReqNos(res.data.stnReqNos || []);

        // ASP ddl_filedata1: fromStore/toBranch/toStore disabled in new mode until STNReq selected
        if (!forEdit) {
          setFromStore("Select");
          setToBranch("Select");
          setToStore("Select");
        }
      }
    } catch (e) {
      console.error("meta error:", e.message);
    } finally {
      setMetaLoading(false);
    }
  }, [branchId, finYearId]);

  // ════════════════════════════════════════════════
  // Code generation
  // ASP: codegeneration() → "STNOUT/0userid/0001"
  // ════════════════════════════════════════════════
  const generateCode = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/generate-code`, {
        params: { branchId, finYearId, loginUserId },
      });
      if (res.data?.success) {
        setStnOutwardNo(res.data.code || "");
      }
    } catch (e) {
      console.error("generate-code error:", e.message);
    }
  }, [branchId, finYearId, loginUserId]);

  // ════════════════════════════════════════════════
  // Load existing data (edit mode)
  // ASP: fileexistingdata() + fillexistingitemsgrid()
  // SPs: usp_GetSTNReqDetails, usp_GetSTNOutExistingItem
  // ════════════════════════════════════════════════
  const loadExistingData = useCallback(async (outwardNo, outwardId) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/existing`, {
        params: { stnOutwardNo: outwardNo, stnOutwardId: outwardId, branchId, finYearId },
      });
      console.log("records", res.data);
      
      if (res.data?.success) {
        const h = res.data.header || {};
        // ASP: fileexistingdata fills dropdowns from DB
        setFromStore(String(h.fromStoreid || "Select"));
        setToBranch(String(h.toBranchid  || "Select"));
        setToStore(String(h.toStoreid    || "Select"));
        setStnReqNo(String(h.stnRequestid || "Select"));
        setCurrentDate(ddmmToISO(h.currentDate || ""));
        setRemarks(h.remarks || "");
        setCloseReq(h.isClosed === true || h.isClosed === "True");

        // Fill edit grid
        const items = (res.data.items || []).map(r => ({
          ...r,
          updateTransferQty: String(r.fld_TransferQty || ""),
        }));
        setEditItems(items);
        setEditVisible(items.length > 0);
      }
    } catch (e) {
      toast.error("Failed to load existing data: " + e.message);
    }
  }, [branchId, finYearId]);

  // ════════════════════════════════════════════════
  // Financial year date validation
  // ASP: checkDate() JS + txt_currentdate_TextChanged
  // ════════════════════════════════════════════════
  const validateCurrentDate = useCallback((dateISO) => {
    const today = getTodayISO();
    if (dateISO > today) {
      toast.warning("You can select only Previous days");
      return false;
    }
    if (finYearStart) {
      const fyISO = ddmmToISO(finYearStart);
      if (dateISO < fyISO) {
        toast.warning(`You can select only Greater than or Equal to Financial Year Date ${finYearStart}`);
        return false;
      }
    }
    if (finYearEnd) {
      const fyEndISO = ddmmToISO(finYearEnd);
      if (dateISO > fyEndISO) {
        toast.warning(`You can select only Less than or Equal to Financial Year End Date ${finYearEnd}`);
        return false;
      }
    }
    return true;
  }, [finYearStart, finYearEnd]);

  // STN Req No change -> auto-fill stores/branch
  // ASP: ddl_stnreq_SelectedIndexChanged
  // SP:  STNReqNoChangedinSTNOW <- takes SelectedItem.TEXT (label), NOT value (id)
  // After selection: fromStore, toStore, toBranch become enabled
  const handleStnReqChange = useCallback(async (val) => {
    setStnReqNo(val);

    if (!val || val === "Select") {
      // Reset and re-disable stores (ASP: ddl_filedata1 behaviour)
      setFromStore("Select");
      setToBranch("Select");
      setToStore("Select");
      setStoresDisabled(true);
      setItemListVisible(false);
      return;
    }

    // ASP: ddl_stnreq.SelectedItem.Text -> get the label (display text) from stnReqNos list
    const selectedReq = stnReqNos.find(r => r.value === val);
    const stnReqText  = selectedReq?.label || val; // fallback to val if label not found

    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/stnreq-change`, {
        params: { stnReqText, finYearId, branchId },
      });
      if (res.data?.success && res.data.row) {
        const r = res.data.row;
        // Node normalizes SP columns to: fromStoreId, fromBranchId, toStoreId, toBranchId
        // ASP: ddl_tostore ← fromStore, ddl_transferbranch ← fromBranch, ddl_fromstore ← ToStore
        setToStore(  r.fromStoreId  ? String(r.fromStoreId)  : "Select");
        setToBranch( r.fromBranchId ? String(r.fromBranchId) : "Select");
        setFromStore(r.toStoreId    ? String(r.toStoreId)    : "Select");
        // ASP: fields become enabled after STNReqNo selected
        setStoresDisabled(false);
      } else {
        setFromStore("Select");
        setToBranch("Select");
        setToStore("Select");
        setStoresDisabled(true);
        setItemListVisible(false);
      }
    } catch (e) {
      console.error("stnreq-change error:", e.message);
    }
  }, [finYearId, stnReqNos]);

  // ════════════════════════════════════════════════
  // From Store change → cascade to-branches + to-stores + STN req nos
  // ASP: ddl_fromstore_SelectedIndexChanged
  // SP:  usp_FromStoreChangedinSTNOW, FillStores, BindSTNRequest
  // ════════════════════════════════════════════════
  const handleFromStoreChange = useCallback(async (val) => {
    setFromStore(val);
    if (!val || val === "Select") return;
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/fromstore-change`, {
        params: { fromStore: val, toBranch, toStore, branchId, finYearId },
      });
      if (res.data?.success) {
        if (res.data.toBranches) setToBranches(res.data.toBranches);
        if (res.data.toStores)   setToStores(res.data.toStores);
        if (res.data.stnReqNos)  setStnReqNos(res.data.stnReqNos);
      }
    } catch (e) {
      console.error("fromstore-change error:", e.message);
    }
  }, [branchId, finYearId, toBranch, toStore]);

  // ════════════════════════════════════════════════
  // To Branch change → fill to-stores + bind STN requests
  // ASP: ddl_transferbranch_SelectedIndexChanged → FillStores + ddl_tostorevaluechanging
  // ════════════════════════════════════════════════
  const handleToBranchChange = useCallback(async (val) => {
    setToBranch(val);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/tobranch-change`, {
        params: { fromStore, toBranch: val, toStore, branchId, finYearId },
      });
      if (res.data?.success) {
        if (res.data.toStores)  setToStores(res.data.toStores);
        if (res.data.stnReqNos) setStnReqNos(res.data.stnReqNos);
      }
    } catch (e) {
      console.error("tobranch-change error:", e.message);
    }
  }, [branchId, finYearId, fromStore, toStore]);

  // ════════════════════════════════════════════════
  // To Store change → bind STN requests
  // ASP: ddl_tostore_SelectedIndexChanged → ddl_tostorevaluechanging → BindSTNRequest
  // ════════════════════════════════════════════════
  const handleToStoreChange = useCallback(async (val) => {
    setToStore(val);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/tostore-change`, {
        params: { fromStore, toBranch, toStore: val, branchId, finYearId },
      });
      if (res.data?.success && res.data.stnReqNos) {
        setStnReqNos(res.data.stnReqNos);
      }
    } catch (e) {
      console.error("tostore-change error:", e.message);
    }
  }, [branchId, finYearId, fromStore, toBranch]);

  // ════════════════════════════════════════════════
  // Search — ASP: Imgbtn_search_Click
  // Validation: stnReqNo != "Select"
  // SP: usp_GetSTNOutSearch
  // ════════════════════════════════════════════════
  const handleSearch = useCallback(async () => {
    if (!stnReqNo || stnReqNo === "Select") {
      toast.warning("Please select STN Req No");
      return;
    }
    setSearchLoading(true);
    setItemListVisible(false);
    setOutwardVisible(false);
    setEditVisible(false);
    setFutureDateItems([]);
    setShowFutureWarn(false);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/search`, {
        params: {
          branchId,
          finYearId,
          fromDate:   fromDate ? isoToDDMM(fromDate) : "",
          toDate:     toDate   ? isoToDDMM(toDate)   : "",
          fromStore:  fromStore !== "Select" ? fromStore : "%",
          toBranch:   toBranch  !== "Select" ? toBranch  : "%",
          toStore:    toStore   !== "Select" ? toStore   : "%",
          stnReqNo:   stnReqNo  !== "Select" ? stnReqNo  : "%",
        },
      });
      if (res.data?.success) {
        const list = res.data.items || [];
        setItemList(list);
        setItemListPage(1);
        setSelectedItemIds(new Set());
        setSelectAllItems(false);
        setItemListVisible(true);
        if (list.length === 0) toast.info("No records found");
      }
    } catch (e) {
      toast.error("Search failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setSearchLoading(false);
    }
  }, [branchId, finYearId, fromDate, toDate, fromStore, toBranch, toStore, stnReqNo]);

  // ════════════════════════════════════════════════
  // Select All Items checkbox (gv_itemlist)
  // ASP: SelectAllRights('chk_select', this.checked)
  // ════════════════════════════════════════════════
  const handleSelectAllItems = (checked) => {
    setSelectAllItems(checked);
    if (checked) {
      setSelectedItemIds(new Set(itemList.map((_, i) => i)));
    } else {
      setSelectedItemIds(new Set());
    }
  };

  const toggleItemSelect = (idx) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      setSelectAllItems(next.size === itemList.length);
      return next;
    });
  };

  // ════════════════════════════════════════════════
  // Add selected items — gv_itemlist "Add" button
  // ASP: gv_itemlist_RowCommand("Add") → fillselecteditemsgrid
  // Validation: currentDate required, stnReqNo required
  // SP: fillselecteditemsgridinSTNOutward + ledger future-date check
  // ════════════════════════════════════════════════
  const handleAddSelectedItems = useCallback(async () => {
    if (selectedItemIds.size === 0) {
      toast.warning("Please select any One");
      return;
    }
    if (!currentDate) {
      toast.warning("Pl enter STN Date and continue");
      return;
    }
    if (!validateCurrentDate(currentDate)) return;

    // Build selected item ids and request ids
    const selectedRows   = itemList.filter((_, i) => selectedItemIds.has(i));
    const selectItemIds  = selectedRows.map(r => r.fld_itemid).join(",");
    const selectReqIds   = selectedRows.map(r => r.fld_STNRequestid).join(",");

    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-entry/fill-selected-items`, {
        params: {
          branchId,
          finYearId,
          selectItemIds,
          selectReqIds,
          fromStore:   fromStore !== "Select" ? fromStore : "",
          currentDate: isoToDDMM(currentDate),
          stnReqNo:    stnReqNo  !== "Select" ? stnReqNo  : "",
        },
      });
      if (res.data?.success) {
        // Future date warning
        if (res.data.futureDateItems?.length > 0) {
          setFutureDateItems(res.data.futureDateItems);
          setShowFutureWarn(true);
        } else {
          setFutureDateItems([]);
          setShowFutureWarn(false);
        }

        const items = (res.data.items || []).map(r => ({
          ...r,
          transferQty: "",
          checked: true,
        }));

        if (items.length === 0) {
          toast.warning("Items may not be available on the selected date.");
          return;
        }

        setOutwardItems(items);
        setOutwardVisible(true);
        setItemListVisible(false); // hide item list after add (like ASP)
      }
    } catch (e) {
      toast.error("Failed to load items: " + (e?.response?.data?.message || e.message));
    }
  }, [branchId, finYearId, selectedItemIds, itemList, currentDate, fromStore, stnReqNo, validateCurrentDate]);

  // ════════════════════════════════════════════════
  // Transfer qty change validation
  // ASP: txt_transferqty_TextChanged
  // Rules: > 0, ≤ pendingQty, ≤ availableStock
  // ════════════════════════════════════════════════
  const handleTransferQtyChange = (idx, val) => {
    setOutwardItems(prev => {
      const next = [...prev];
      const item = { ...next[idx] };
      item.transferQty = val;

      if (val !== "") {
        const transqty    = safeFloat(val);
        const pendingqty  = safeFloat(item.fld_pendingqty);
        const availableqty= safeFloat(item.fld_closing_stock);

        if (transqty === 0) {
          toast.warning("Transfer Qty should not be Zero");
          item.transferQty = "";
        } else if (transqty > pendingqty) {
          toast.warning("Please Enter the Transfer Qty less than or equal to Available Stock and Required Qty");
          item.transferQty = "";
        } else if (availableqty < transqty) {
          toast.warning("Please Enter the Transfer Qty less than or equal to Available Stock and Required Qty");
          item.transferQty = "";
        }
      }
      next[idx] = item;
      return next;
    });
  };

  // ── Edit mode transfer qty change ──
  const handleEditTransferQtyChange = (idx, val) => {
    setEditItems(prev => {
      const next = [...prev];
      const item = { ...next[idx] };
      console.log("items for edit :", item);
      
      item.updateTransferQty = val;

      if (val !== "") {
       const transqty     = safeFloat(val);
const reqQty       = safeFloat(item.fld_closing_stock); // max = available stock in edit mode
const availableqty = safeFloat(item.fld_closing_stock);

if (transqty === 0) {
  toast.warning("Transfer Qty should not be Zero");
  item.updateTransferQty = "";
} else if (availableqty < transqty) {
  toast.warning("Please give a Proper input");
  item.updateTransferQty = "";
}
      }
      next[idx] = item;
      return next;
    });
  };

  // ════════════════════════════════════════════════
  // Submit NEW outward
  // ASP: gv_outwardlist_RowCommand("SUBMIT") → INV_STNOutward.InsertSTNOutward
  // Return codes: 1=already outwarded, 2=master fail, 3=dup key(retry), 4=stock changed, 5=detail fail, 6=success
  // ════════════════════════════════════════════════
  const handleSubmitNew = useCallback(async (retryCount = 0) => {
    // Validation
    if (fromStore === "Select") { toast.warning("Please select From store field"); return; }
    if (toBranch  === "Select") { toast.warning("Please select Transfer Branch Field"); return; }
    if (toStore   === "Select") { toast.warning("Please select To store field"); return; }
    if (!currentDate)           { toast.warning("Please enter STN Date"); return; }
    if (!validateCurrentDate(currentDate)) return;
    if (stnReqNo  === "Select") { toast.warning("Please select STN Req No"); return; }

    // Check at least one item is checked
    const checkedItems = outwardItems.filter(item => item.checked);
    if (checkedItems.length === 0) {
      toast.warning("Please select any One");
      return;
    }

    // Validate each checked item has transferQty
    for (const item of checkedItems) {
      if (!item.transferQty || safeFloat(item.transferQty) <= 0) {
        toast.warning(`Please enter Transfer Qty for ${item.fld_itemname}`);
        return;
      }
    }

    setSubmitLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/stn-outward-entry/submit`, {
        branchId,
        finYearId,
        fromStore,
        toBranchId: toBranch,
        stnReqNo,
        remarks,
        loginUserId,
        currentDate: isoToDDMM(currentDate),
        closeReq,
        items: checkedItems.map(item => ({
          fld_itemid:        item.fld_itemid,
          fld_branchitemid:  item.fld_branchitemid,
          fld_STNRequestdetid: item.fld_STNRequestdetid,
          transferQty:       item.transferQty,
          fld_closing_stock: item.fld_closing_stock,
          fld_pendingqty:    item.fld_pendingqty,
          serialNos:         item.serialNos || [],
        })),
      });

      const rv = res.data?.returnValue;

      switch (rv) {
        case 1:
          toast.error(`STN Requisition already outwarded by ${res.data.issuePerson || "another user"}`);
          setTimeout(() => navigate("/Inventory/STNListing"), 2000);
          break;
        case 2:
          toast.error("Please refresh and try again");
          break;
        case 3:
          // Duplicate key — retry up to 10 times
          if (retryCount < 10) {
            toast.info("Retrying due to duplicate key...");
            await new Promise(r => setTimeout(r, 300));
            handleSubmitNew(retryCount + 1);
          } else {
            toast.error("Transaction not saved. Please refresh and try again");
          }
          break;
        case 4:
          toast.error("STN Item(s) current Quantity changed. Please refresh and retry");
          break;
        case 5:
          toast.error("Transaction not saved. Please refresh and try again");
          break;
        case 6:
          toast.success(`STN Outward saved successfully (Req: ${stnReqNos.find(r => r.value === stnReqNo)?.label || stnReqNo})`);
          setTimeout(() => navigate("/Inventory/STNListing"), 1500);
          break;
        default:
          toast.error(res.data?.message || "Unknown error");
      }
    } catch (e) {
      toast.error("Submit failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setSubmitLoading(false);
    }
  }, [branchId, finYearId, fromStore, toBranch, toStore, stnReqNo, remarks, loginUserId, currentDate, closeReq, outwardItems, stnReqNos, validateCurrentDate, navigate]);

  // ════════════════════════════════════════════════
  // Submit EDIT outward
  // ASP: gv_outlistedit_RowCommand("SUBMIT") → UpdateSTNoutward + UpdateSTNoutwardDet
  // ════════════════════════════════════════════════
  const handleSubmitEdit = useCallback(async () => {
    if (!currentDate) { toast.warning("Please enter STN Date"); return; }
    if (!validateCurrentDate(currentDate)) return;

    // Validate all edit items have transferQty
    for (const item of editItems) {
      if (!item.updateTransferQty || safeFloat(item.updateTransferQty) <= 0) {
        toast.warning(`Please give input for ${item.fld_itemname}`);
        return;
      }
    }

    setSubmitLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/stn-outward-entry/update`, {
        branchId,
        finYearId,
        stnOutwardNo:   sessionOutwardNo,
        stnOutwardId:   sessionOutwardId,
        stnReqNo,
        remarks,
        loginUserId,
        username,
        currentDate:    isoToDDMM(currentDate),
        closeReq,
        items: editItems.map(item => ({
          fld_itemid:           item.fld_itemid,
          fld_branchitemid:     item.fld_branchitemid,
          fld_STN_Outwarddetid: item.fld_STN_Outwarddetid,
          updateTransferQty:    item.updateTransferQty,
          fld_closing_stock:    item.fld_closing_stock,
          fld_Qty:              item.fld_Qty,
          fld_fifoids:          item.fld_fifoids,
          fld_fifoqtys:         item.fld_fifoqtys,
          fld_fifoprices:       item.fld_fifoprices,
          serialNos:            item.serialNos || [],
        })),
      });

      if (res.data?.success) {
        toast.success("STN Outward updated successfully");
        setTimeout(() => navigate("/Inventory/STNListing"), 1500);
      } else {
        toast.error(res.data?.message || "Update failed");
      }
    } catch (e) {
      toast.error("Update failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setSubmitLoading(false);
    }
  }, [branchId, finYearId, sessionOutwardNo, sessionOutwardId, stnReqNo, remarks, loginUserId, currentDate, closeReq, editItems, validateCurrentDate, navigate]);

  // ─────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <HeaderBar title="STN Outward" />
      <ToastContainer position="top-right" autoClose={3500} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen px-4 py-6 ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}>

        {/* ── Page Title + Back ── */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/Inventory/STNListing")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition
                ${isDark ? "border-white/10 hover:bg-white/5 text-gray-300" : "border-gray-200 hover:bg-gray-100 text-gray-600"}`}>
              <ArrowLeft size={13} /> Back
            </button>
            <h1 className={`text-lg font-bold uppercase tracking-wider ${isDark ? "text-white" : "text-gray-800"}`}>
              {isEditMode ? "STN Outward — Edit" : "STN Outward"}
            </h1>
          </div>
          <span className={`text-xs font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            INFRA SCREEN : A-26/1
          </span>
        </div>

        {/* ══════════════════════════════════════════════
            HEADER FORM CARD
        ══════════════════════════════════════════════ */}
        <div className={`${cardBase} mb-5`}>
          <div className={`px-5 py-3 ${primaryBtnClass}`}>
            <h2 className="text-sm font-bold text-white tracking-wider uppercase">
              {isEditMode ? "Edit STN Outward" : "New STN Outward"}
            </h2>
          </div>

          <div className="p-5 space-y-4">
            {/* Row 1 — STN Outward No */}
            {showStnOutwardNoRow && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelBase}>STN Outward No</label>
                  <div className={`mt-1 px-3 py-2 rounded-xl border text-sm font-bold text-purple-600 ${isDark ? "bg-gray-800/40 border-white/10" : "bg-gray-50 border-gray-200"}`}>
                    {stnOutwardNo || "—"}
                  </div>
                </div>
              </div>
            )}

            {/* Row 2 — From Date + To Date (search filter) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>From Date</label>
                <input type="date" value={fromDate} max={getTodayISO()}
                  onChange={e => setFromDate(e.target.value)}
                  disabled={isEditMode}
                  className={`${isEditMode ? inputDisabled : inputBase} mt-1`} />
              </div>
              <div>
                <label className={labelBase}>To Date</label>
                <input type="date" value={toDate} max={getTodayISO()}
                  onChange={e => setToDate(e.target.value)}
                  disabled={isEditMode}
                  className={`${isEditMode ? inputDisabled : inputBase} mt-1`} />
              </div>
            </div>

            {/* Row 3 — From Store + To Store */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>From Store</label>
                <select
                  value={fromStore}
                  onChange={e => handleFromStoreChange(e.target.value)}
                  disabled={isEditMode || storesDisabled}
                  className={`${(isEditMode || storesDisabled) ? inputDisabled : inputBase} mt-1`}>
                  <option value="Select">Select</option>
                  {fromStores.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelBase}>To Store</label>
                <select
                  value={toStore}
                  onChange={e => handleToStoreChange(e.target.value)}
                  disabled={isEditMode || storesDisabled}
                  className={`${(isEditMode || storesDisabled) ? inputDisabled : inputBase} mt-1`}>
                  <option value="Select">Select</option>
                  {toStores.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 4 — Date (current) + To Branch */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>Date</label>
                <input type="date" value={currentDate} max={getTodayISO()}
                  onChange={e => {
                    setCurrentDate(e.target.value);
                    validateCurrentDate(e.target.value);
                  }}
                  disabled={isEditMode}
                  className={`${isEditMode ? inputDisabled : inputBase} mt-1`} />
              </div>
              <div>
                <label className={labelBase}>To Branch</label>
                <select
                  value={toBranch}
                  onChange={e => handleToBranchChange(e.target.value)}
                  disabled={isEditMode || storesDisabled}
                  className={`${(isEditMode || storesDisabled) ? inputDisabled : inputBase} mt-1`}>
                  <option value="Select">Select</option>
                  {toBranches.map(b => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 5 — STN Req No + Remarks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>
                  STN Req No <span className="text-red-500">*</span>
                </label>
                <select
                  value={stnReqNo}
                  onChange={e => handleStnReqChange(e.target.value)}
                  disabled={isEditMode}
                  className={`${isEditMode ? inputDisabled : inputBase} mt-1`}>
                  <option value="Select">Select</option>
                  {stnReqNos.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {/* Close radio — ASP: rbtn_close (shown only in certain cases) */}
                {isEditMode && (
                  <label className={`flex items-center gap-2 mt-2 text-xs cursor-pointer ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    <input type="checkbox" checked={closeReq} onChange={e => setCloseReq(e.target.checked)}
                      className="w-3.5 h-3.5 rounded" />
                    Close
                  </label>
                )}
              </div>
              <div>
                <label className={labelBase}>Remarks</label>
                <textarea
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  rows={3}
                  className={`${inputBase} mt-1 resize-none`}
                  placeholder="Enter remarks..." />
              </div>
            </div>

            {/* Search Button — hidden in edit mode */}
            {searchVisible && !isEditMode && (
              <div className="flex justify-center pt-1">
                <button
                  onClick={handleSearch}
                  disabled={searchLoading}
                  className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass} text-white px-8 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}>
                  {searchLoading
                    ? <RefreshCcw size={15} className="animate-spin" />
                    : <Search size={15} />}
                  {searchLoading ? "Searching..." : "Search"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════════
            FUTURE DATE WARNING (lst_items + lbl_mess)
        ══════════════════════════════════════════════ */}
        {showFutureWarn && futureDateItems.length > 0 && (
          <div className={`mb-4 p-4 rounded-2xl border flex gap-3
            ${isDark ? "bg-yellow-900/20 border-yellow-700/40" : "bg-yellow-50 border-yellow-200"}`}>
            <AlertCircle size={18} className="text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-yellow-600 mb-2">
                Items Are Already outwarded/Issued on Future Date So Can't Issue On Previous Date
              </p>
              <ul className="space-y-1">
                {futureDateItems.map((item, i) => (
                  <li key={i} className={`text-xs px-2 py-1 rounded ${isDark ? "bg-yellow-900/30 text-yellow-300" : "bg-yellow-100 text-yellow-700"}`}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <button onClick={() => setShowFutureWarn(false)} className="text-yellow-500 hover:text-yellow-700 flex-shrink-0">
              <X size={15} />
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            GRID 1: gv_itemlist — Search Results
        ══════════════════════════════════════════════ */}
        {itemListVisible && (
          <div className={`${cardBase} mb-5`}>
            <div className={`px-5 py-3 ${primaryBtnClass}`}>
              <h2 className="text-sm font-bold text-white tracking-wider uppercase">Search Results</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={`${thBase} text-center`}>S.No</th>
                    <th className={thBase}>Item Name</th>
                    <th className={thBase}>Item Code</th>
                    <th className={`${thBase} text-center`}>
                      <label className="flex items-center gap-1.5 cursor-pointer justify-center">
                        <input type="checkbox" checked={selectAllItems}
                          onChange={e => handleSelectAllItems(e.target.checked)}
                          className="w-3.5 h-3.5" />
                        <span>Select All</span>
                      </label>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {itemListPaged.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={`${tdBase} text-center py-10 text-gray-400`}>No records found</td>
                    </tr>
                  ) : itemListPaged.map((row, idx) => {
                    const globalIdx = (itemListPage - 1) * ITEM_PAGE_SIZE + idx;
                    const isSelected = selectedItemIds.has(globalIdx);
                    return (
                      <tr key={globalIdx}
                        className={`border-t transition cursor-pointer
                          ${isDark ? "border-white/5" : "border-gray-100"}
                          ${globalIdx % 2 === 0
                            ? (isDark ? "bg-gray-800/20 hover:bg-white/5" : "bg-gray-50/60 hover:bg-blue-50/40")
                            : (isDark ? "hover:bg-white/5" : "hover:bg-blue-50/40")}
                          ${isSelected ? (isDark ? "bg-blue-900/30" : "bg-blue-50") : ""}`}
                        onClick={() => toggleItemSelect(globalIdx)}>
                        <td className={`${tdBase} text-center`}>{globalIdx + 1}</td>
                        <td className={tdBase}>{row.fld_itemname}</td>
                        <td className={tdBase}>{row.fld_itemcode}</td>
                        <td className={`${tdBase} text-center`}>
                          <input type="checkbox" checked={isSelected}
                            onChange={() => toggleItemSelect(globalIdx)}
                            onClick={e => e.stopPropagation()}
                            className="w-3.5 h-3.5 cursor-pointer" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Add button (footer) */}
            <div className="flex justify-center py-3 border-t border-gray-100 dark:border-white/10">
              <button
                onClick={handleAddSelectedItems}
                className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass} text-white px-6 py-2 rounded-xl text-sm font-semibold shadow transition`}>
                <CheckSquare size={15} /> Add Selected
              </button>
            </div>

            {/* Pagination */}
            {itemList.length > ITEM_PAGE_SIZE && (
              <div className={`px-4 py-3 border-t text-xs flex items-center justify-between flex-wrap gap-2
                ${isDark ? "border-white/10 text-gray-400" : "border-gray-100 text-gray-500"}`}>
                <span>
                  Showing {Math.min((itemListPage-1)*ITEM_PAGE_SIZE+1, itemList.length)}–{Math.min(itemListPage*ITEM_PAGE_SIZE, itemList.length)} of {itemList.length}
                </span>
                <Pagination page={itemListPage} totalPages={itemListTotal} onPage={p => setItemListPage(p)} />
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════
            GRID 2: gv_outwardlist — New Entry Items
        ══════════════════════════════════════════════ */}
        {outwardVisible && outwardItems.length > 0 && (
          <div className={`${cardBase} mb-5`}>
            <div className={`px-5 py-3 ${primaryBtnClass}`}>
              <h2 className="text-sm font-bold text-white tracking-wider uppercase">Outward Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={`${thBase} text-center`}>S.No</th>
                    <th className={thBase}>Item Code</th>
                    <th className={thBase}>Item Name</th>
                    <th className={thBase}>Part No</th>
                    <th className={thBase}>UOM</th>
                    <th className={`${thBase} text-center`}>Avail Stock</th>
                    <th className={`${thBase} text-center`}>Qty as on Date</th>
                    <th className={`${thBase} text-center`}>Pending Qty</th>
                    <th className={`${thBase} text-center`}>Required Qty</th>
                    <th className={`${thBase} text-center`}>Transfer Qty <span className="text-red-300">*</span></th>
                    <th className={`${thBase} text-center`}>Serial No</th>
                    <th className={`${thBase} text-center`}>
                      <label className="flex items-center gap-1 cursor-pointer justify-center">
                        <input type="checkbox"
                          checked={outwardItems.every(i => i.checked)}
                          onChange={e => setOutwardItems(prev => prev.map(i => ({ ...i, checked: e.target.checked })))}
                          className="w-3.5 h-3.5" />
                        <span>Select All</span>
                      </label>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {outwardItems.map((item, idx) => (
                    <tr key={idx}
                      className={`border-t transition
                        ${isDark ? "border-white/5" : "border-gray-100"}
                        ${idx % 2 === 0
                          ? (isDark ? "bg-gray-800/20" : "bg-gray-50/60")
                          : ""}`}>
                      <td className={`${tdBase} text-center`}>{idx + 1}</td>
                      <td className={tdBase}>{item.fld_itemcode}</td>
                      <td className={tdBase}>{item.fld_itemname}</td>
                      <td className={tdBase}>{item.fld_itemPartNo || "—"}</td>
                      <td className={tdBase}>{item.fld_uomname}</td>
                      <td className={`${tdBase} text-center`}>{item.fld_closing_stock}</td>
                      <td className={`${tdBase} text-center`}>{item.datewiseqty ?? "—"}</td>
                      <td className={`${tdBase} text-center`}>{item.fld_pendingqty}</td>
                      <td className={`${tdBase} text-center`}>{item.fld_Qty}</td>
                      <td className={`${tdBase} text-center`}>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={item.transferQty}
                          onChange={e => handleTransferQtyChange(idx, e.target.value)}
                          className={`w-20 px-2 py-1.5 rounded-lg border text-xs text-center outline-none focus:ring-2 focus:ring-blue-400
                            ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-800"}`}
                          placeholder="0"
                        />
                      </td>
                      <td className={`${tdBase} text-center`}>
                        {/* ASP: lnkSerialNo — enabled if fld_IsSerialNo */}
                        {item.fld_IsSerialNo ? (
                          <button
                            onClick={() => setSerialModal({ open: true, itemId: item.fld_itemid, fromStoreId: fromStore, qty: parseInt(item.transferQty) || 0, mode: "new", rowIdx: idx })}
                            className={`text-xs font-semibold underline ${item.serialno === "Yes" ? "text-blue-500 hover:text-blue-700" : "text-gray-400 cursor-not-allowed"}`}
                            disabled={!item.serialno === "Yes"}>
                            {item.serialno || "No"}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className={`${tdBase} text-center`}>
                        <input type="checkbox" checked={!!item.checked}
                          onChange={e => setOutwardItems(prev => prev.map((it, i) => i === idx ? { ...it, checked: e.target.checked } : it))}
                          className="w-3.5 h-3.5 cursor-pointer" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Submit button (footer) */}
            <div className="flex justify-center py-3 border-t border-gray-100 dark:border-white/10">
              <button
                onClick={() => handleSubmitNew()}
                disabled={submitLoading}
                className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass} text-white px-8 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}>
                {submitLoading
                  ? <RefreshCcw size={15} className="animate-spin" />
                  : <Send size={15} />}
                {submitLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            GRID 3: gv_outlistedit — Edit Mode Items
        ══════════════════════════════════════════════ */}
        {isEditMode && editVisible && editItems.length > 0 && (
          <div className={`${cardBase} mb-5`}>
            <div className={`px-5 py-3 flex items-center gap-2 ${primaryBtnClass}`}>
              <Edit3 size={15} className="text-white/80" />
              <h2 className="text-sm font-bold text-white tracking-wider uppercase">Edit Outward Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={`${thBase} text-center`}>S.No</th>
                    <th className={thBase}>Item Code</th>
                    <th className={thBase}>Item Name</th>
                    <th className={thBase}>Part No</th>
                    <th className={thBase}>UOM</th>
                    <th className={`${thBase} text-center`}>Avail Stock</th>
                    <th className={`${thBase} text-center`}>Requested Qty</th>
                    <th className={`${thBase} text-center`}>Transfer Qty <span className="text-red-300">*</span></th>
                    <th className={`${thBase} text-center`}>Serial No</th>
                  </tr>
                </thead>
                <tbody>
                  {editItems.map((item, idx) => (
                    <tr key={idx}
                      className={`border-t transition
                        ${isDark ? "border-white/5" : "border-gray-100"}
                        ${idx % 2 === 0
                          ? (isDark ? "bg-gray-800/20" : "bg-gray-50/60")
                          : ""}`}>
                      <td className={`${tdBase} text-center`}>{idx + 1}</td>
                      <td className={tdBase}>{item.fld_itemcode}</td>
                      <td className={tdBase}>{item.fld_itemname}</td>
                      <td className={tdBase}>{item.fld_itemPartNo || "—"}</td>
                      <td className={tdBase}>{item.fld_uomname}</td>
                      <td className={`${tdBase} text-center`}>{item.fld_closing_stock}</td>
                      <td className={`${tdBase} text-center`}>{item.fld_Qty}</td>
                      <td className={`${tdBase} text-center`}>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={item.updateTransferQty}
                          onChange={e => handleEditTransferQtyChange(idx, e.target.value)}
                          className={`w-20 px-2 py-1.5 rounded-lg border text-xs text-center outline-none focus:ring-2 focus:ring-blue-400
                            ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-800"}`}
                        />
                      </td>
                      <td className={`${tdBase} text-center`}>
                        {item.fld_IsSerialNo ? (
                          <button
                            onClick={() => setSerialModal({ open: true, itemId: item.fld_itemid, fromStoreId: fromStore, qty: parseInt(item.updateTransferQty) || 0, mode: "edit", rowIdx: idx })}
                            className="text-xs font-semibold underline text-blue-500 hover:text-blue-700">
                            {item.serialno || "No"}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Submit Edit (footer) */}
            {/* ASP: gv_outlistedit is disabled in edit mode from listing, enabled for submit */}
            <div className="flex justify-center py-3 border-t border-gray-100 dark:border-white/10">
              <button
                onClick={handleSubmitEdit}
                disabled={submitLoading}
                className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass} text-white px-8 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}>
                {submitLoading
                  ? <RefreshCcw size={15} className="animate-spin" />
                  : <Send size={15} />}
                {submitLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            SERIAL NO MODAL (replaces ASP popup window)
            ASP: window.open('SerialNoEntry.aspx?...')
        ══════════════════════════════════════════════ */}
        {serialModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className={`rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden
              ${isDark ? "bg-gray-800 border border-white/10" : "bg-white border border-gray-200"}`}>
              <div className={`px-5 py-3 flex items-center justify-between ${primaryBtnClass}`}>
                <div className="flex items-center gap-2">
                  <Info size={15} className="text-white/80" />
                  <h3 className="text-sm font-bold text-white">Serial No Entry</h3>
                </div>
                <button onClick={() => setSerialModal({ open: false })} className="text-white/70 hover:text-white">
                  <X size={16} />
                </button>
              </div>
              <div className="p-5">
                <p className={`text-sm mb-3 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Item ID: <strong>{serialModal.itemId}</strong> | Transfer Qty: <strong>{serialModal.qty}</strong>
                </p>
                <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Serial No entry is handled via the SerialNoEntry page.<br />
                  Please open{" "}
                  <a
                    href={`/Inventory/SerialNoEntry?item=${serialModal.itemId}-${serialModal.fromStoreId}-${branchId}&qty=${serialModal.qty}&pro=STNO&f=${serialModal.mode === "edit" ? 2 : 1}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500 underline font-semibold">
                    Serial No Entry
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </LayoutWrapper>
  );
}